import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CalendarDays } from "lucide-react";
import AttributeField from "./AttributeField";
import {
  FORM_TYPES,
  NUMBER_DATA_TYPE,
  SUBJECT_DATA_TYPES,
  TEXT_ANCHORS,
  coerceBoolean,
  updateVariableAttr,
} from "../../../common/CertificateOnboarding/utils/variables";
import { INPUT_CLASS } from "../../../common/CertificateOnboarding/utils/styles";

function parseDateValue(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateValue(date) {
  if (!date) return "";
  return date.toISOString();
}

function SubjectVariableEditor({
  variableKey,
  attrs,
  setVariableConfig,
  t,
  hideSvgPositioning = false,
}) {
  const dataType = attrs["data-type"] || "textField";
  const isDateType = dataType === "date";
  const isNumberType = dataType === NUMBER_DATA_TYPE;
  const isFillLocked =
    isNumberType && coerceBoolean(attrs["data-fill"] ?? false);

  const onAttrChange = (attrKey) => (value) => {
    updateVariableAttr(setVariableConfig, variableKey, attrKey, value);
  };

  const dataTypes = isNumberType
    ? [...SUBJECT_DATA_TYPES, NUMBER_DATA_TYPE]
    : SUBJECT_DATA_TYPES;
  const typeOptions = dataTypes.map((val) => ({
    value: val,
    label: t(`certificateOnboarding:data_type_${val}`),
  }));

  const formTypeOptions = FORM_TYPES.map((val) => ({
    value: val,
    label: t(`certificateOnboarding:form_type_${val}`),
  }));

  const anchorOptions = TEXT_ANCHORS.map((val) => ({
    value: val,
    label: t(`certificateOnboarding:text_anchor_${val}`),
  }));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2 space-y-1.5">
        <label
          htmlFor={`${variableKey}-default-value`}
          className="text-sm font-medium text-cfSignInSignUpInputLabelText"
        >
          {t("certificateOnboarding:attr_data_default_value")}
        </label>
        {isDateType ? (
          <div className="relative">
            <DatePicker
              id={`${variableKey}-default-value`}
              selected={parseDateValue(attrs["data-default-value"])}
              onChange={(date) =>
                onAttrChange("data-default-value")(formatDateValue(date))
              }
              dateFormat="dd/MM/yyyy"
              placeholderText={t(
                "certificateOnboarding:date_picker_placeholder",
              )}
              className={`${INPUT_CLASS} w-full pr-11`}
              wrapperClassName="w-full"
              popperClassName="z-50"
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              yearDropdownItemNumber={15}
              scrollableYearDropdown
            />
            <CalendarDays className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500 pointer-events-none" />
          </div>
        ) : (
          <input
            type={dataType === "email" ? "email" : "text"}
            id={`${variableKey}-default-value`}
            value={attrs["data-default-value"] ?? ""}
            onChange={(e) => onAttrChange("data-default-value")(e.target.value)}
            className={INPUT_CLASS}
          />
        )}
      </div>

      <AttributeField
        id={`${variableKey}-description`}
        label={t("certificateOnboarding:attr_data_description")}
        value={attrs["data-description"]}
        onChange={onAttrChange("data-description")}
      />

      <AttributeField
        id={`${variableKey}-key`}
        label={t("certificateOnboarding:attr_data_key")}
        value={attrs["data-key"]}
        onChange={onAttrChange("data-key")}
      />

      <AttributeField
        id={`${variableKey}-label`}
        label={t("certificateOnboarding:attr_data_label")}
        value={attrs["data-label"]}
        onChange={onAttrChange("data-label")}
      />

      <AttributeField
        id={`${variableKey}-name`}
        label={t("certificateOnboarding:attr_data_name")}
        value={attrs["data-name"]}
        onChange={onAttrChange("data-name")}
      />

      <AttributeField
        id={`${variableKey}-type`}
        label={t("certificateOnboarding:attr_data_type")}
        type="select"
        value={dataType}
        onChange={onAttrChange("data-type")}
        options={typeOptions}
        disabled={isFillLocked}
      />

      <AttributeField
        id={`${variableKey}-form-type`}
        label={t("certificateOnboarding:attr_data_form_type")}
        type="select"
        value={attrs["data-form-type"] ?? "subject"}
        onChange={onAttrChange("data-form-type")}
        options={formTypeOptions}
      />

      <AttributeField
        id={`${variableKey}-required`}
        label={t("certificateOnboarding:attr_data_required")}
        type="toggle"
        value={attrs["data-required"]}
        onChange={onAttrChange("data-required")}
      />

      {!hideSvgPositioning && (
        <div className="md:col-span-2 rounded-xl border border-cfCardBorder bg-cfPrimaryMuted/25 px-4 py-4 space-y-4">
          <div>
            <h4 className="text-sm font-medium text-cfHeadingText">
              {t("certificateOnboarding:attr_position_section_title")}
            </h4>
            <p className="text-xs text-cfGlobalText mt-1 leading-relaxed">
              {t("certificateOnboarding:attr_coordinates_hint")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <AttributeField
              id={`${variableKey}-text-anchor`}
              label={t("certificateOnboarding:attr_text_anchor")}
              type="select"
              value={attrs["text-anchor"] ?? "start"}
              onChange={onAttrChange("text-anchor")}
              options={anchorOptions}
            />

            <AttributeField
              id={`${variableKey}-coordinate-x`}
              label={t("certificateOnboarding:attr_coordinate_x")}
              value={attrs.x ?? ""}
              onChange={onAttrChange("x")}
              placeholder={t(
                "certificateOnboarding:attr_coordinate_placeholder",
              )}
              inputMode="decimal"
            />

            <AttributeField
              id={`${variableKey}-coordinate-y`}
              label={t("certificateOnboarding:attr_coordinate_y")}
              value={attrs.y ?? ""}
              onChange={onAttrChange("y")}
              placeholder={t(
                "certificateOnboarding:attr_coordinate_placeholder",
              )}
              inputMode="decimal"
            />
          </div>
        </div>
      )}

      <AttributeField
        id={`${variableKey}-max-characters`}
        label={t("certificateOnboarding:attr_max_characters")}
        type="number"
        value={attrs["max-characters"] ?? ""}
        onChange={onAttrChange("max-characters")}
        min={1}
      />

      {!hideSvgPositioning && (
        <AttributeField
          id={`${variableKey}-number-of-lines`}
          label={t("certificateOnboarding:attr_number_of_lines")}
          type="number"
          value={attrs["number-of-lines"] ?? ""}
          onChange={onAttrChange("number-of-lines")}
          min={1}
        />
      )}

      {isDateType && (
        <AttributeField
          id={`${variableKey}-format`}
          label={t("certificateOnboarding:attr_data_format")}
          value={attrs["data-format"] ?? ""}
          onChange={onAttrChange("data-format")}
          placeholder="DD/MM/YYYY"
        />
      )}
    </div>
  );
}

export default SubjectVariableEditor;
